/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState, useRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {useCategory} from '@salesforce/commerce-sdk-react'

// Components
import {
    Box,
    Center,
    Flex,
    Spinner,
    Stack,

    // Hooks
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import {ListMenuPopover} from './partial/list-menu-popover'
import Link from '@salesforce/retail-react-app/app/components/link'

/* Context */
import {useGlobalNavigation, useGlobalHeader} from '../../contexts/global-elements-context'
import {useHeaderContext} from '../../contexts/header-context'

/* Utils */
import {
    mergeCMSCategoryReplacements,
    applyCategoryAddReplace,
    getMegaMenuLinkCtaData
} from '../../components/optimizely/utils/utils'
import {renderComponentsSwitch} from '../../components/optimizely/utils/contentRenderer'
import {isGDILinkUsable, GDI_LINK_TYPES} from '../optimizely/gdi/utils/gdi-link-resolver'
import {getMegaMenuLinkSx} from '../optimizely/gdi/utils/gdi-cta-style'
import {useGetLinkData} from '../../contexts/category-url-context'
import GDILinkBlock from '../optimizely/gdi/assets/gdiLinkBlock'
import {adaptCtaForNav, withoutColorProps} from './partial/list-menu-helpers'
import useEffectiveHeaderVariant from '../header/partial/useEffectiveHeaderVariant'

// Others
import {categoryUrlBuilder} from '../../utils/url'

// Constants
const MAXIMUM_NUMBER_COLUMNS = 6
const OPEN_DELAY = 300
const CLOSE_DELAY = 300
const CLOSE_ANIMATION_MS = 200

/**
 * A CategoryAddReplace `Replace` fallback category is "popover-worthy" when it
 * has API subcategories OR any CMS-driven mega menu content attached to the
 * same category ID. Without the CMS checks, replacing a category whose only
 * content is a CMS Mega Menu would silently degrade the trigger to a plain
 * link and the authored menu would never open.
 */
const hasFallbackPopoverContent = (category, itemsCountKey, itemsKey) => {
    if (!category) return false
    const apiChildrenCount = category[itemsCountKey] || category[itemsKey]?.length || 0
    if (apiChildrenCount > 0) return true
    if (category.hasCMSMegaMenu && category.CMSMegaMenuData) return true
    if (category.cmsMegaMenuMode) return true
    if (
        Array.isArray(category.cmsContentTileIllustrations) &&
        category.cmsContentTileIllustrations.length > 0
    )
        return true
    if (
        Array.isArray(category.MegaMenuIlustrationBlockList) &&
        category.MegaMenuIlustrationBlockList.length > 0
    )
        return true
    return false
}

/**
 * Invisible helper that prefetches a CMS-referenced category (named via a
 * CategoryAddReplace `showSubcategoriesCategoryId`) by id — so the nav can
 * decide whether to open a popover BEFORE the user hovers. These references
 * usually point at a subcategory (L2+) that is absent from the shallow
 * (L1-only) pre-loaded nav tree; without this we cannot know whether the
 * category exists (it may 404) or has any content to render.
 *
 * Reports the outcome through `onResolved(categoryId, {status, category})`:
 *   - status 'ready' → the category resolved and has subcategories to render
 *   - status 'empty' → the category 404'd or has nothing to render
 * Renders nothing.
 */
const RefCategoryPrefetch = ({categoryId, onResolved}) => {
    const {data, isLoading, isError} = useCategory({
        parameters: {id: categoryId, levels: 2}
    })

    useEffect(() => {
        if (isLoading) return
        const hasChildren =
            (data?.onlineSubCategoriesCount || 0) > 0 ||
            (Array.isArray(data?.categories) && data.categories.length > 0)
        onResolved(categoryId, {
            status: !isError && data && hasChildren ? 'ready' : 'empty',
            category: data || null
        })
    }, [categoryId, isLoading, isError, data, onResolved])

    return null
}

RefCategoryPrefetch.propTypes = {
    categoryId: PropTypes.string.isRequired,
    onResolved: PropTypes.func.isRequired
}

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const ListMenu = ({
    root,
    contentComponent,
    categories,
    itemsKey,
    itemsCountKey,
    maxColumns = MAXIMUM_NUMBER_COLUMNS
}) => {
    const [ariaBusy, setAriaBusy] = useState(true)
    const [activeLink, setActiveLink] = useState()
    const [activeItemId, setActiveItemId] = useState(null)
    const [isClosing, setIsClosing] = useState(false)
    const [keyboardOpenedId, setKeyboardOpenedId] = useState(null)
    const intl = useIntl()
    const {data: globalNavigationData} = useGlobalNavigation()
    const {data: headerData} = useGlobalHeader()
    const {category, shouldShowHeader, pageType} = useHeaderContext()
    const {isTransparent: isTransparentHeader} = useEffectiveHeaderVariant()
    const baseStyle = useMultiStyleConfig('ListMenu', {})
    const items = root?.[itemsKey]
    const getLinkData = useGetLinkData()

    // CMS-driven first-level mutations (Prepend / Append / Replace) for WebDesktop.
    // Only the first seven items are visible in the bar, mirroring the slice below.
    // The full `items` list is passed as the lookup pool so an entry's
    // GDIGlobalComponentsPageShowSubcategories can resolve an L1 category that
    // falls outside the seven-item window.
    const firstLevelItems = applyCategoryAddReplace(
        items?.slice(0, 7),
        headerData?.CategoryAddReplace,
        'WebDesktop',
        items
    )

    // A CategoryAddReplace CTA may reference a category via
    // `showSubcategoriesCategoryId` that is NOT in the shallow (L1-only) nav
    // tree, yielding a `__cmsSubcategoriesRef` stub. Prefetch each referenced
    // id so we only open its popover when the category actually resolves with
    // content — never for a 404 or an empty category.
    const [refCategoryStatus, setRefCategoryStatus] = useState({})
    const handleRefResolved = useCallback((categoryId, result) => {
        setRefCategoryStatus((prev) => {
            const existing = prev[categoryId]
            if (
                existing &&
                existing.status === result.status &&
                existing.category === result.category
            ) {
                return prev
            }
            return {...prev, [categoryId]: result}
        })
    }, [])

    const refCategoryIds = Array.from(
        new Set(
            (firstLevelItems || [])
                .filter(
                    (it) =>
                        it?.__cmsItemType === 'cta' && it.fallbackCategory?.__cmsSubcategoriesRef
                )
                .map((it) => it.fallbackCategory.id)
                .filter(Boolean)
        )
    )

    // Resolve a CTA's fallback category into the object to render and whether it
    // should open a popover. For `__cmsSubcategoriesRef` stubs this depends on
    // the prefetch outcome (open only when 'ready'); otherwise it uses the
    // in-tree content check.
    const resolveCtaFallback = (fallbackCategory) => {
        if (!fallbackCategory) return {fallback: null, hasPopover: false}
        if (fallbackCategory.__cmsSubcategoriesRef) {
            const status = refCategoryStatus[fallbackCategory.id]
            return {
                fallback: status?.category || fallbackCategory,
                hasPopover: status?.status === 'ready'
            }
        }
        return {
            fallback: fallbackCategory,
            hasPopover: hasFallbackPopoverContent(fallbackCategory, itemsCountKey, itemsKey)
        }
    }

    const openTimerRef = useRef(null)
    const closeTimerRef = useRef(null)
    const closeAnimTimerRef = useRef(null)
    const isOpenRef = useRef(false)
    const isClosingRef = useRef(false)
    const skipOpenAnimationRef = useRef(false)
    const toggleButtonRefs = useRef({})

    // Active state only applies on PLP (parentCategoryTree[1] is the top-level ancestor).
    const isPLP = pageType === 'product-list'
    const activeTopLevelCategoryId = isPLP
        ? category?.parentCategoryTree?.length > 1
            ? category.parentCategoryTree[1].id
            : category?.id
        : null

    const clearAllTimers = () => {
        clearTimeout(openTimerRef.current)
        clearTimeout(closeTimerRef.current)
        clearTimeout(closeAnimTimerRef.current)
    }

    const doImmediateClose = () => {
        clearAllTimers()
        setActiveItemId(null)
        setIsClosing(false)
        setKeyboardOpenedId(null)
        isOpenRef.current = false
        isClosingRef.current = false
    }

    const doClose = () => {
        clearAllTimers()
        isClosingRef.current = true
        setIsClosing(true)
        setKeyboardOpenedId(null)
        closeAnimTimerRef.current = setTimeout(() => {
            setActiveItemId(null)
            setIsClosing(false)
            isClosingRef.current = false
            isOpenRef.current = false
        }, CLOSE_ANIMATION_MS)
    }

    const handleItemMouseEnter = (id) => {
        clearTimeout(closeTimerRef.current)
        if (isOpenRef.current || isClosingRef.current) {
            // Already open or in close animation — switch immediately with no delay or open animation
            clearAllTimers()
            isClosingRef.current = false
            skipOpenAnimationRef.current = true
            setIsClosing(false)
            isOpenRef.current = true
            setActiveItemId(id)
        } else {
            // Not open — apply open delay, then animate in
            openTimerRef.current = setTimeout(() => {
                isOpenRef.current = true
                skipOpenAnimationRef.current = false
                setActiveItemId(id)
            }, OPEN_DELAY)
        }
    }

    const handleMenuMouseLeave = () => {
        clearTimeout(openTimerRef.current)
        closeTimerRef.current = setTimeout(() => {
            doClose()
        }, CLOSE_DELAY)
    }

    const handleMenuMouseEnter = () => {
        clearTimeout(closeTimerRef.current)
    }

    // Callback ref so each item registers its toggle button element
    const setToggleButtonRef = useCallback((id, el) => {
        toggleButtonRefs.current[id] = el
    }, [])

    // Keyboard toggle: open immediately (no delay), or close if already this item
    const handleKeyboardToggle = (id) => {
        if (activeItemId === id && isOpenRef.current) {
            doImmediateClose()
        } else {
            clearAllTimers()
            isClosingRef.current = false
            skipOpenAnimationRef.current = false
            setIsClosing(false)
            isOpenRef.current = true
            setKeyboardOpenedId(id)
            setActiveItemId(id)
        }
    }

    // Esc: close and return keyboard focus to the toggle button that opened the menu
    const handleEscapeKey = (id) => {
        doImmediateClose()
        toggleButtonRefs.current[id]?.focus()
    }

    // Tab-out: close without moving focus (focus has already moved naturally)
    const handleFocusLeave = () => {
        doImmediateClose()
    }

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    // Close immediately when the header scrolls out of view
    useEffect(() => {
        if (!shouldShowHeader && (isOpenRef.current || isClosingRef.current)) {
            doImmediateClose()
        }
    }, [shouldShowHeader])

    // Close immediately when the window loses or regains focus (prevents ghost hover state)
    useEffect(() => {
        const blockOpen = () => {
            doImmediateClose()
        }
        window.addEventListener('blur', blockOpen)
        window.addEventListener('focus', blockOpen)
        return () => {
            window.removeEventListener('blur', blockOpen)
            window.removeEventListener('focus', blockOpen)
        }
    }, [])

    return (
        <nav
            id="list-menu"
            className="main-nav"
            data-gtm-nav="menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            {items ? (
                <Stack {...baseStyle.stackContainer}>
                    {refCategoryIds.map((cid) => (
                        <RefCategoryPrefetch
                            key={`ref-prefetch-${cid}`}
                            categoryId={cid}
                            onResolved={handleRefResolved}
                        />
                    ))}
                    {firstLevelItems?.map?.((entry) => {
                        // CMS-injected CTA from CategoryAddReplace. When the block is a
                        // GDICtaBlock styled as "Mega Menu Link" (or unstyled), render
                        // as a plain nav link so it inherits the navigation's existing
                        // spacing/hover/animation. Otherwise let the block render with
                        // its own authored styling via the shared GDI renderer.
                        if (entry.__cmsItemType === 'cta') {
                            const megaLinkData = getMegaMenuLinkCtaData(entry.block, 'WebDesktop')
                            if (megaLinkData) {
                                const linkBlock = megaLinkData.linkBlock
                                const {fallback: fallbackCategory, hasPopover: fallbackHasPopover} =
                                    resolveCtaFallback(entry.fallbackCategory)
                                const linkUsable = isGDILinkUsable(linkBlock, getLinkData)
                                // Mega Menu Link styling shared across all branches —
                                // applied either to the anchor rendered by GDILinkBlock
                                // (for any usable LinkType) or to the plain fallback link.
                                const navLinkStyle = {
                                    ...baseStyle.listMenuTriggerLink,
                                    position: 'relative',
                                    sx: getMegaMenuLinkSx(),
                                    ...megaLinkData.styleProps,
                                    _hover: {
                                        textDecoration: 'none',
                                        ...megaLinkData.hoverStyleProps
                                    }
                                }
                                // Wrap the visible label in a [data-mml-text] span so
                                // getMegaMenuLinkSx()'s ::before scale animation has a
                                // text-only anchor element (same approach gdiCtaBlock
                                // uses for shortcode-parsed content). Without this the
                                // animation never triggers on L0 CategoryAddReplace
                                // CTAs whose text is rendered as a plain string.
                                const wrappedText = (
                                    <span data-mml-text="">{megaLinkData.text}</span>
                                )

                                // Fallback popover content available — render a popover
                                // trigger so subcategories (or CMS mega-menu content)
                                // of the Replaced category remain reachable on hover.
                                // When the CTA has its own authored link we render
                                // GDILinkBlock as the popover's `customTrigger` so the
                                // click navigates via the CMS link while hover still
                                // opens the menu. When there's no link, the popover's
                                // default trigger (a link to the fallback category)
                                // is used and only the CTA's text/styling is layered on.
                                if (fallbackHasPopover) {
                                    const ctaPopoverId = fallbackCategory.id
                                    const isThisCtaOpen = activeItemId === ctaPopoverId
                                    const customTrigger = linkUsable ? (
                                        <GDILinkBlock
                                            {...linkBlock}
                                            AriaLabel={
                                                megaLinkData.ariaLabel || linkBlock?.AriaLabel
                                            }
                                            linkProps={navLinkStyle}
                                        >
                                            {wrappedText}
                                        </GDILinkBlock>
                                    ) : undefined
                                    return (
                                        <Box
                                            key={entry.key}
                                            onMouseEnter={() => handleItemMouseEnter(ctaPopoverId)}
                                            onMouseLeave={handleMenuMouseLeave}
                                        >
                                            <ListMenuPopover
                                                key={ctaPopoverId}
                                                maxColumns={maxColumns}
                                                item={fallbackCategory}
                                                name={megaLinkData.text}
                                                items={fallbackCategory?.[itemsKey]}
                                                itemsKey={itemsKey}
                                                categories={categories}
                                                contentComponent={contentComponent}
                                                isActive={activeTopLevelCategoryId === ctaPopoverId}
                                                isOpen={isThisCtaOpen}
                                                isKeyboardOpen={
                                                    isThisCtaOpen &&
                                                    keyboardOpenedId === ctaPopoverId
                                                }
                                                isClosing={isThisCtaOpen && isClosing}
                                                skipOpenAnimation={
                                                    isThisCtaOpen && skipOpenAnimationRef.current
                                                }
                                                onClose={doImmediateClose}
                                                onMenuMouseEnter={handleMenuMouseEnter}
                                                onMenuMouseLeave={handleMenuMouseLeave}
                                                onKeyboardToggle={() =>
                                                    handleKeyboardToggle(ctaPopoverId)
                                                }
                                                onEscapeKey={() => handleEscapeKey(ctaPopoverId)}
                                                onFocusLeave={handleFocusLeave}
                                                toggleButtonRef={(el) =>
                                                    setToggleButtonRef(ctaPopoverId, el)
                                                }
                                                triggerDisplayName={wrappedText}
                                                triggerAriaLabel={
                                                    megaLinkData.ariaLabel || undefined
                                                }
                                                triggerLinkProps={megaLinkData.styleProps}
                                                triggerHoverProps={megaLinkData.hoverStyleProps}
                                                triggerSx={getMegaMenuLinkSx()}
                                                customTrigger={customTrigger}
                                            />
                                        </Box>
                                    )
                                }

                                // No popover content. When the CTA has an authored
                                // link, render it via GDILinkBlock so the full
                                // LinkType handling (Internal/External/Product/
                                // Category/CMS Page/Custom Drawer/System Drawer ID)
                                // is preserved.
                                if (linkUsable) {
                                    return (
                                        <Box
                                            key={entry.key}
                                            {...baseStyle.listMenuTriggerContainer}
                                        >
                                            <GDILinkBlock
                                                {...linkBlock}
                                                AriaLabel={
                                                    megaLinkData.ariaLabel || linkBlock?.AriaLabel
                                                }
                                                linkProps={navLinkStyle}
                                            >
                                                {wrappedText}
                                            </GDILinkBlock>
                                            <Box
                                                aria-hidden="true"
                                                flexShrink={0}
                                                width="8px"
                                                height="16px"
                                            />
                                        </Box>
                                    )
                                }

                                // No link and no popover content — navigate to the
                                // referenced category's URL when available, otherwise
                                // render a non-navigable label (e.g. Prepend/Append
                                // CTA without a CMS link and no fallback category).
                                const href = fallbackCategory
                                    ? categoryUrlBuilder(fallbackCategory, categories)
                                    : '#'
                                return (
                                    <Box key={entry.key} {...baseStyle.listMenuTriggerContainer}>
                                        <Link
                                            as={RouteLink}
                                            to={href}
                                            aria-label={megaLinkData.ariaLabel || undefined}
                                            {...navLinkStyle}
                                        >
                                            {wrappedText}
                                        </Link>
                                        <Box
                                            aria-hidden="true"
                                            flexShrink={0}
                                            width="8px"
                                            height="16px"
                                        />
                                    </Box>
                                )
                            }
                            // Non "Mega Menu Link" CTA — render via the shared GDI
                            // renderer so the block keeps its authored button styling
                            // (Button Solid, etc.).
                            //   - fallback has popover content → wrap the styled
                            //     button in ListMenuPopover (hover opens menu). When
                            //     the CTA has its own link, that link still drives
                            //     click navigation since the rendered block is used
                            //     as the popover's `customTrigger`.
                            //   - no popover content, usable CMS link → render the
                            //     block as-is, GDICtaBlock handles navigation.
                            //   - no popover content, no usable link → synthesize an
                            //     Internal Link to the referenced category so the
                            //     button navigates somewhere meaningful.
                            const linkBlock =
                                entry.block?.GDICtaBlockLink?.[0]?.ContentLink?.Expanded || null
                            const {fallback: fallbackCategory, hasPopover: fallbackHasPopover} =
                                resolveCtaFallback(entry.fallbackCategory)
                            const linkUsable = isGDILinkUsable(linkBlock, getLinkData)

                            if (fallbackHasPopover) {
                                const ctaPopoverId = fallbackCategory.id
                                const isThisCtaOpen = activeItemId === ctaPopoverId
                                return (
                                    <Box
                                        key={entry.key}
                                        onMouseEnter={() => handleItemMouseEnter(ctaPopoverId)}
                                        onMouseLeave={handleMenuMouseLeave}
                                    >
                                        <ListMenuPopover
                                            maxColumns={maxColumns}
                                            item={fallbackCategory}
                                            name={
                                                entry.block?.GDICtaBlockStylesPerViewType?.find?.(
                                                    (style) => style?.CtaText
                                                )?.CtaText || ''
                                            }
                                            items={fallbackCategory?.[itemsKey]}
                                            itemsKey={itemsKey}
                                            categories={categories}
                                            contentComponent={contentComponent}
                                            isActive={activeTopLevelCategoryId === ctaPopoverId}
                                            isOpen={isThisCtaOpen}
                                            isKeyboardOpen={
                                                isThisCtaOpen && keyboardOpenedId === ctaPopoverId
                                            }
                                            isClosing={isThisCtaOpen && isClosing}
                                            skipOpenAnimation={
                                                isThisCtaOpen && skipOpenAnimationRef.current
                                            }
                                            onClose={doImmediateClose}
                                            onMenuMouseEnter={handleMenuMouseEnter}
                                            onMenuMouseLeave={handleMenuMouseLeave}
                                            onKeyboardToggle={() =>
                                                handleKeyboardToggle(ctaPopoverId)
                                            }
                                            onEscapeKey={() => handleEscapeKey(ctaPopoverId)}
                                            onFocusLeave={handleFocusLeave}
                                            toggleButtonRef={(el) =>
                                                setToggleButtonRef(ctaPopoverId, el)
                                            }
                                            customTrigger={renderComponentsSwitch(
                                                adaptCtaForNav(entry.block)
                                            )}
                                        />
                                    </Box>
                                )
                            }
                            const blockToRender =
                                !linkUsable && fallbackCategory
                                    ? {
                                          ...entry.block,
                                          GDICtaBlockLink: [
                                              {
                                                  ContentLink: {
                                                      Expanded: {
                                                          __typename: 'GDILinkBlock',
                                                          LinkType: GDI_LINK_TYPES.INTERNAL,
                                                          LinkReference: categoryUrlBuilder(
                                                              fallbackCategory,
                                                              categories
                                                          )
                                                      }
                                                  }
                                              }
                                          ]
                                      }
                                    : entry.block
                            return (
                                <Box key={entry.key} {...baseStyle.listMenuTriggerContainer}>
                                    {renderComponentsSwitch(adaptCtaForNav(blockToRender))}
                                </Box>
                            )
                        }

                        const item = entry.category
                        const processedCategoryWithCMS = mergeCMSCategoryReplacements(
                            item,
                            globalNavigationData
                        )
                        const {id, name} = processedCategoryWithCMS
                        const itemsCount =
                            processedCategoryWithCMS[itemsCountKey] ||
                            processedCategoryWithCMS[itemsKey]?.length ||
                            0
                        const cmsCategoryReplacement =
                            processedCategoryWithCMS.CategoryReplacement?.ContentLink?.Expanded
                        // A category should render its mega menu popover whenever it
                        // has SFCC subcategories OR any CMS-authored mega menu content
                        // (page-level MegaMenu config, legacy CategoryReplacement mega
                        // menu block, or ShopMenuContent illustrations). Without this
                        // check, categories with CMS content but no SFCC children
                        // (e.g. grg_early_access) would silently fall back to a plain
                        // link and the authored content would never render.
                        const hasCmsMegaMenuContent = Boolean(
                            processedCategoryWithCMS.cmsMegaMenuMode ||
                                processedCategoryWithCMS.hasCMSMegaMenu ||
                                processedCategoryWithCMS.CMSMegaMenuData ||
                                processedCategoryWithCMS.MegaMenuIlustrationBlockList?.length
                        )
                        const shouldRenderPopover = itemsCount > 0 || hasCmsMegaMenuContent
                        const isActive = activeTopLevelCategoryId === id
                        const isThisItemOpen = activeItemId === id

                        return (
                            <Box
                                key={id}
                                onMouseEnter={
                                    shouldRenderPopover ? () => handleItemMouseEnter(id) : undefined
                                }
                                onMouseLeave={
                                    shouldRenderPopover ? handleMenuMouseLeave : undefined
                                }
                            >
                                {shouldRenderPopover ? (
                                    <ListMenuPopover
                                        key={id}
                                        maxColumns={maxColumns}
                                        item={processedCategoryWithCMS}
                                        name={name}
                                        items={item?.[itemsKey]}
                                        itemsKey={itemsKey}
                                        categories={categories}
                                        contentComponent={contentComponent}
                                        isActive={isActive}
                                        isOpen={isThisItemOpen}
                                        isKeyboardOpen={isThisItemOpen && keyboardOpenedId === id}
                                        isClosing={isThisItemOpen && isClosing}
                                        skipOpenAnimation={
                                            isThisItemOpen && skipOpenAnimationRef.current
                                        }
                                        onClose={doImmediateClose}
                                        onMenuMouseEnter={handleMenuMouseEnter}
                                        onMenuMouseLeave={handleMenuMouseLeave}
                                        onKeyboardToggle={() => handleKeyboardToggle(id)}
                                        onEscapeKey={() => handleEscapeKey(id)}
                                        onFocusLeave={handleFocusLeave}
                                        toggleButtonRef={(el) => setToggleButtonRef(id, el)}
                                    />
                                ) : (
                                    <Box {...baseStyle.listMenuTriggerContainer}>
                                        <Link
                                            as={RouteLink}
                                            to={categoryUrlBuilder(item, categories)}
                                            onMouseOver={setActiveLink.bind(this, id)}
                                            onMouseOut={setActiveLink.bind(this)}
                                            {...baseStyle.listMenuTriggerLink}
                                            {...{name: name + ' __'}}
                                            {...(activeLink === id || isActive
                                                ? baseStyle.listMenuTriggerLinkActive
                                                : {})}
                                            /* CMS Props - suppressed on transparent header */
                                            color={
                                                isTransparentHeader
                                                    ? undefined
                                                    : cmsCategoryReplacement?.TextColor
                                            }
                                            backgroundColor={
                                                isTransparentHeader
                                                    ? undefined
                                                    : cmsCategoryReplacement?.TextBackgroundColor
                                            }
                                        >
                                            {cmsCategoryReplacement?.OverrideName || name}
                                        </Link>
                                        {/* Invisible spacer — matches the 8px toggle button on items with submenus */}
                                        <Box
                                            aria-hidden="true"
                                            flexShrink={0}
                                            width="8px"
                                            height="16px"
                                        />
                                    </Box>
                                )}
                            </Box>
                        )
                    })}
                </Stack>
            ) : (
                <Center {...baseStyle.spinnerContainer}>
                    <Spinner size="lg" />
                </Center>
            )}

            {/* Single overlay shared across all popovers */}
            {activeItemId !== null && <Box {...baseStyle.popoverOverlay} />}
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The maximum number of columns that we want to use per row in the menu.
     */
    maxColumns: PropTypes.number,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Customize the component used to render the list menu item
     */
    contentComponent: PropTypes.elementType,
    /**
     * Map of categoryId -> full category data (includes c_relativeUrl) from SSR
     */
    categories: PropTypes.object
}

export {ListMenu}
export {ListMenuTrigger} from './partial/list-menu-trigger'
export {ListMenuPopover} from './partial/list-menu-popover'
export {ListMenuContent} from './partial/list-menu-content'
